// ============================================
// Prompt Configuration Types
// Recursive improvement system for AI Nurse prompts
// ============================================

/** A versioned nurse prompt template stored in Firestore */
export interface NursePromptConfig {
  versionId: string;
  version: number;
  isActive: boolean;
  createdAt: string;
  source: 'manual' | 'ai_optimizer' | 'initial' | 'teaching_session';
  /** The complete prompt template with {{PLACEHOLDER}} tokens for dynamic data */
  template: string;
  /** Human-readable description of changes from previous version */
  changelog: string;
  /** Performance tracking — updated after each run */
  stats: {
    runsCount: number;
    avgPassRate: number;
    bestPassRate: number;
    worstPassRate: number;
  };
}

/** Result of the post-run prompt optimizer analysis */
export interface PromptAnalysis {
  analysisId: string;
  runId: string;
  promptVersion: number;
  createdAt: string;
  /** Overall analysis of failure patterns */
  failurePatterns: string;
  /** Specific suggested edits to the prompt template */
  suggestions: PromptSuggestion[];
  /** Whether a new version was auto-generated */
  newVersionGenerated: boolean;
  newVersionId?: string;
}

/** A single suggested edit to the prompt template */
export interface PromptSuggestion {
  id: string;
  section: string; // e.g. "VOICE RULES", "CLINICAL RULES", "CONVERSATION FLOW"
  currentText: string;
  suggestedText: string;
  reasoning: string;
  /** Which check failures this suggestion addresses */
  addressesChecks: string[];
  status: 'pending' | 'accepted' | 'rejected';
}

/** Template placeholder tokens used in the stored prompt */
export const PROMPT_PLACEHOLDERS = {
  PATIENT_INFO: '{{PATIENT_INFO}}',
  CONDITIONS: '{{CONDITIONS}}',
  MEDICATIONS: '{{MEDICATIONS}}',
  LAB_RESULTS: '{{LAB_RESULTS}}',
  SCENARIO: '{{SCENARIO}}',
  PATIENT_CONTEXT: '{{PATIENT_CONTEXT}}',
  CONVERSATION_MODES: '{{CONVERSATION_MODES}}',
  AH_HA_LINE: '{{AH_HA_LINE}}',
  FORMULARY_RECS: '{{FORMULARY_RECS}}',
  BIOMARKER_CHECKLIST: '{{BIOMARKER_CHECKLIST}}',
} as const;

/** A suggested trait slider adjustment */
export interface TraitAdjustment {
  trait: string;     // key from NurseTraitProfile (empathy, dataDensity, etc.)
  from: number;      // current value
  to: number;        // suggested value
  reasoning: string;
}

/** A single message in the optimizer chat */
export interface OptimizerChatMessage {
  role: 'user' | 'optimizer';
  content: string;
  suggestions?: PromptSuggestion[];
  traitAdjustments?: TraitAdjustment[];
  timestamp: string;
}
